// src/screens/academique/EspaceFormation.jsx
import React, { useState, useEffect, createContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FormationHome from './FormationHome';
import MesCours from './MesCours';
import MesResultats from './MesResultats';
import Labellisation from './Labellisation';
import CourseViewer from './CourseViewer';
import QcmModal from './QcmModal';
import LabelingTest from './LabelingTest';
import FormationMainContainer from '../../components/FormationMainContainer';
import FormationService from '../../services/FormationService';

export const FormationContext = createContext(null);

export default function EspaceFormation() {
  const navigate = useNavigate();
  
  // États
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('home');
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [courses, setCourses] = useState([]);
  const [qcmResults, setQcmResults] = useState([]);
  const [studentClass, setStudentClass] = useState(null);
  const [matieres, setMatieres] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);
  const [notificationBadge, setNotificationBadge] = useState(0);
  const [stats, setStats] = useState({ total_qcm: 0, avg_score: 0 });
  
  // Modals state
  const [courseViewerOpen, setCourseViewerOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [qcmModalOpen, setQcmModalOpen] = useState(false);
  const [selectedCourseForQcm, setSelectedCourseForQcm] = useState(null);
  const [labelingTestOpen, setLabelingTestOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  
  // Définir loadData avec useCallback pour pouvoir l'utiliser dans refreshData
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Récupérer USER_DETAILS depuis localStorage
      let userData = null;
      const storedUserDetails = localStorage.getItem('USER_DETAILS');
      
      if (storedUserDetails && storedUserDetails !== 'undefined' && storedUserDetails !== 'null') {
        try {
          userData = JSON.parse(storedUserDetails);
        } catch (parseError) {
          console.error('Erreur lors du parsing USER_DETAILS:', parseError);
        }
      }
      
      if (!userData) {
        userData = {};
      }
      
      // S'assurer que first_name et last_name sont définis
      if (userData) {
        if (!userData.first_name) {
          userData.first_name = userData.name ? userData.name.split(' ')[0] : 'Étudiant';
        }
        if (!userData.last_name) {
          userData.last_name = userData.name ? userData.name.split(' ').slice(1).join(' ') : '';
        }
        if (!userData.display_name) {
          userData.display_name = `${userData.first_name} ${userData.last_name}`.trim();
        }
      }
      
      const studentId = userData.person_id || userData.user_id;
      
      // Récupérer les données via FormationService
      if (studentId) {
        // Récupérer les matières avec leurs cours
        const matieresResult = await FormationService.getMatieres(studentId);
        if (matieresResult && matieresResult.status === true) {
          setMatieres(matieresResult.data || []);
          
          // Extraire tous les cours des matières
          const allCourses = [];
          (matieresResult.data || []).forEach(matiere => {
            if (matiere.cours && matiere.cours.length) {
              matiere.cours.forEach(course => {
                allCourses.push({
                  ...course,
                  matiere_nom: matiere.nom,
                  matiere_id: matiere.id
                });
              });
            }
          });
          setCourses(allCourses);
        }
        
        // Récupérer les résultats QCM
        const resultsResult = await FormationService.getResults(studentId);
        if (resultsResult && resultsResult.status === true) {
          setQcmResults(resultsResult.data?.results || []);
          setStats(resultsResult.data?.stats || { total_qcm: 0, avg_score: 0 });
        }
      }
      
      // Pour les tests de labellisation (à adapter selon votre backend)
      const testsData = [];
      setAvailableTests(testsData);
      setNotificationBadge(testsData.filter(t => !t.has_submitted).length);
      
      setStudent(userData);
      setStudentClass(null);
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), 3500);
  };
  
  const switchView = (view) => {
    setActiveView(view);
  };
  
  const openCourse = (course) => {
    setSelectedCourse(course);
    setCourseViewerOpen(true);
  };
  
  const openQcm = (course) => {
    setSelectedCourseForQcm(course);
    setQcmModalOpen(true);
  };
  
  const openLabelingTest = (test, session) => {
    setSelectedTest({ test, session });
    setLabelingTestOpen(true);
  };
  
  const handleQcmComplete = async (result) => {
    const studentId = student?.person_id || student?.user_id;
    
    if (studentId) {
      const submitResult = await FormationService.submitQcmResult({
        student_id: studentId,
        course_id: result.courseId,
        score: result.score,
        total_questions: result.totalQuestions,
        correct_answers: result.correctAnswers,
        answers: result.answers
      });
      
      if (submitResult && submitResult.status === true) {
        setQcmResults(prev => [...prev, {
          course_id: result.courseId,
          score: result.score,
          total_questions: result.totalQuestions,
          correct_answers: result.correctAnswers,
          created_at: new Date().toISOString()
        }]);
        
        // Mettre à jour les statistiques
        const newResults = [...qcmResults, result];
        const newAvg = Math.round(newResults.reduce((a, r) => a + (r.score || 0), 0) / newResults.length);
        setStats({ total_qcm: newResults.length, avg_score: newAvg });
        
        showToast(`QCM terminé! Score: ${result.score}%`, 'success');
      } else {
        showToast(submitResult?.message || 'Erreur lors de l\'enregistrement', 'error');
      }
    }
    
    setQcmModalOpen(false);
  };
  
  const handleLabelingComplete = (result) => {
    setAvailableTests(prev => prev.map(t => 
      t.id === result.testId ? { ...t, has_submitted: true, submitted_results: [{ global_score: result.score, passed: result.passed }] } : t
    ));
    showToast(`Test terminé! Score: ${result.score}%`, result.passed ? 'success' : 'warning');
    setLabelingTestOpen(false);
  };
  
  // Fonction pour rafraîchir les données
  const refreshData = useCallback(() => {
    loadData();
  }, [loadData]);
  
  // Construire l'objet student complet pour le contexte
  const contextStudent = student || {
    first_name: 'Étudiant',
    last_name: '',
    name: 'Étudiant',
    email: '',
    direction_name: '',
    service_name: '',
    function_name: '',
    registration_number: '',
    person_id: ''
  };
  
  // Valeur du contexte pour les enfants
  const contextValue = {
    student: contextStudent,
    courses,
    qcmResults,
    studentClass,
    matieres,
    availableTests,
    notificationBadge,
    stats,
    activeView,
    switchView,
    showToast,
    openCourse,
    openQcm,
    openLabelingTest,
    refreshData
  };
  
  // Valeur pour le FormationMainContainer
  const containerValue = {
    student: contextStudent,
    studentClass,
    notificationBadge,
    activeView,
    switchView
  };
  
  const renderView = () => {
    switch(activeView) {
      case 'home': return <FormationHome onSwitchView={switchView} />;
      case 'courses': return <MesCours />;
      case 'results': return <MesResultats />;
      case 'labeling': return <Labellisation />;
      default: return <FormationHome onSwitchView={switchView} />;
    }
  };
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        fontFamily: "'Inter', sans-serif",
        background: '#F5F7FA'
      }}>
        <div>
          <div style={{ 
            width: 48, 
            height: 48, 
            border: '3px solid #E0E0E0', 
            borderTopColor: '#0056A4', 
            borderRadius: '50%', 
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto'
          }}></div>
          <p style={{ marginTop: 16, color: '#6C757D' }}>Chargement de votre espace...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  return (
    <FormationContext.Provider value={contextValue}>
      <FormationMainContainer title="Espace Tutoriels" {...containerValue}>
        {/* Toast */}
        {toast.show && (
          <div style={{
            position: 'fixed',
            top: 80,
            right: 20,
            zIndex: 99998,
            background: '#FFFFFF',
            borderLeft: `4px solid ${toast.type === 'success' ? '#28A745' : toast.type === 'error' ? '#DC3545' : '#0056A4'}`,
            borderRadius: 8,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontSize: '0.875rem',
            maxWidth: 340
          }}>
            <i className={`bi bi-${toast.type === 'success' ? 'check-circle-fill' : toast.type === 'error' ? 'x-circle-fill' : 'info-circle-fill'}`}></i>
            <span>{toast.message}</span>
          </div>
        )}
        
        {/* Contenu principal */}
        {renderView()}
        
        {/* Modals */}
        <CourseViewer 
          open={courseViewerOpen}
          course={selectedCourse}
          onClose={() => setCourseViewerOpen(false)}
        />
        
        <QcmModal
          open={qcmModalOpen}
          course={selectedCourseForQcm}
          onClose={() => setQcmModalOpen(false)}
          onComplete={handleQcmComplete}
        />
        
        <LabelingTest
          open={labelingTestOpen}
          test={selectedTest?.test}
          session={selectedTest?.session}
          student={student}
          onClose={() => setLabelingTestOpen(false)}
          onComplete={handleLabelingComplete}
        />
      </FormationMainContainer>
    </FormationContext.Provider>
  );
}